import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AI_EDIT_INSTRUCTION_MAX_LENGTH } from "@nexus/contracts";
import { Button, Icon, Input, Textarea, cn } from "@nexus/ui";

import { HighlightText } from "@/features/opportunities/components/HighlightText";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

export const PERSONA_SCRIPT_CELL_FLASH_MS = 2_200;
export const PERSONA_SCRIPT_COPY_RESET_MS = 1_400;

const TOOLBAR_BTN_CLASS =
  "grid size-6 place-items-center min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-6 [@media(hover:hover)_and_(pointer:fine)]:min-w-6";

export interface PersonaScriptEditableCellProps {
  value: string;
  canEdit: boolean;
  isEditing?: boolean;
  isAiOpen?: boolean;
  isAiBusy?: boolean;
  isFlashing?: boolean;
  isFlashingAi?: boolean;
  isSaving?: boolean;
  rows?: number;
  /** Defaults true for script cells; brief cells opt out when no AI handler is available. */
  showAi?: boolean;
  emptyLabel?: string;
  onStartEdit?: () => void;
  onStopEdit?: () => void;
  onStartAi?: () => void;
  onStopAi?: () => void;
  onApplyAi?: (instruction: string) => void;
  onSave: (next: string) => void;
  highlightQuery?: string;
}

export function PersonaScriptEditableCell({
  value,
  canEdit,
  isEditing = false,
  isAiOpen = false,
  isAiBusy = false,
  isFlashing = false,
  isFlashingAi = false,
  isSaving = false,
  rows = 4,
  showAi = true,
  emptyLabel,
  onStartEdit,
  onStopEdit,
  onStartAi,
  onStopAi,
  onApplyAi,
  onSave,
  highlightQuery = "",
}: PersonaScriptEditableCellProps) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [draft, setDraft] = useState(value);
  const [instruction, setInstruction] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { copied, copy } = useCopyToClipboard({ resetMs: PERSONA_SCRIPT_COPY_RESET_MS });

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [isEditing, value]);

  useEffect(() => {
    if (!isEditing) return;
    textareaRef.current?.focus();
  }, [isEditing]);

  useEffect(() => {
    if (!isAiOpen) return;
    setInstruction("");
  }, [isAiOpen]);

  function startEdit() {
    if (!canEdit || isSaving || isAiBusy) return;
    setDraft(value);
    onStartEdit?.();
  }

  function cancelEdit() {
    setDraft(value);
    onStopEdit?.();
  }

  function commitEdit() {
    if (!canEdit || isSaving) return;
    if (draft === value) {
      onStopEdit?.();
      return;
    }
    onSave(draft);
    onStopEdit?.();
  }

  function startAi() {
    if (!canEdit || isSaving || isAiBusy) return;
    onStartAi?.();
  }

  function applyAi() {
    const next = instruction.trim();
    if (!canEdit || isAiBusy || !next) return;
    onApplyAi?.(next);
  }

  async function handleCopy() {
    await copy(value);
  }

  if (isAiBusy) {
    return (
      <div
        className="flex items-center gap-2 text-[12.5px] text-[var(--ai-text)]"
        onClick={(event) => event.stopPropagation()}
      >
        <Icon
          name="loader"
          size="xs"
          colorVar="--ai-text"
          className="animate-spin"
          aria-hidden
        />
        {t("personaOpportunities.scripts.cell.rewriting", "Rewriting…")}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2" onClick={(event) => event.stopPropagation()}>
        <Textarea
          ref={textareaRef}
          value={draft}
          rows={rows}
          disabled={isSaving}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
            }
          }}
          className="min-h-0 resize-y border-[1.5px] border-primary text-[12.5px] leading-relaxed shadow-none focus-visible:border-primary focus-visible:ring-0"
          aria-label={t("personaOpportunities.scripts.cell.editAria", "Edit cell")}
        />
        <div className="flex gap-1.5">
          <Button type="button" size="xs" disabled={isSaving} onClick={commitEdit}>
            {t("personaOpportunities.scripts.cell.save", "Save")}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="secondary"
            disabled={isSaving}
            onClick={cancelEdit}
          >
            {t("personaOpportunities.scripts.cell.cancel", "Cancel")}
          </Button>
        </div>
      </div>
    );
  }

  if (isAiOpen) {
    const canApply = instruction.trim().length > 0;
    return (
      <div
        className="space-y-1.5 rounded-sm border-[1.5px] border-[var(--ai)] bg-[var(--ai-fill-light)] p-2.5"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-[10px] font-medium uppercase tracking-[0.06em] text-[var(--ai-text)]">
          {t("personaOpportunities.scripts.cell.aiLabel", "AI edit this cell")}
        </p>
        <p className="whitespace-pre-wrap text-[11.5px] leading-snug text-foreground opacity-75">
          {value.trim() ? value : t("personaOpportunities.scripts.emptyValue", "—")}
        </p>
        <Input
          autoFocus
          value={instruction}
          maxLength={AI_EDIT_INSTRUCTION_MAX_LENGTH}
          placeholder={t(
            "personaOpportunities.scripts.cell.aiPlaceholder",
            "e.g. make it shorter, open with a question…",
          )}
          aria-label={t("personaOpportunities.scripts.cell.aiEditAria", "AI edit this cell")}
          onChange={(event) => setInstruction(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onStopAi?.();
            }
            if (event.key === "Enter") {
              event.preventDefault();
              applyAi();
            }
          }}
        />
        <div className="flex gap-1.5">
          <Button
            type="button"
            size="xs"
            disabled={!canApply}
            className="bg-[var(--ai-text)] text-white hover:bg-[var(--ai-text)]/90"
            onClick={applyAi}
          >
            {t("personaOpportunities.scripts.cell.aiApply", "Apply")}
          </Button>
          <Button type="button" size="xs" variant="secondary" onClick={onStopAi}>
            {t("personaOpportunities.scripts.cell.cancel", "Cancel")}
          </Button>
        </div>
      </div>
    );
  }

  const display = value.trim()
    ? <HighlightText text={value} query={highlightQuery} />
    : (emptyLabel ?? t("personaOpportunities.scripts.emptyValue", "—"));
  const showToolbar = isHovered;

  return (
    <div
      className={cn(
        "relative min-h-11 rounded-sm px-2 py-1.5",
        canEdit && "hover:bg-muted/60",
        isFlashing && "persona-script-cell-flash",
        isFlashingAi && "persona-script-cell-flash-ai",
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {canEdit ? (
        <button
          type="button"
          className="w-full cursor-text whitespace-pre-wrap text-left leading-relaxed text-foreground"
          aria-label={t("personaOpportunities.scripts.cell.editAria", "Edit cell")}
          onClick={startEdit}
        >
          {display}
        </button>
      ) : (
        <p className="whitespace-pre-wrap leading-relaxed text-foreground">{display}</p>
      )}
      {showToolbar ? (
        <div
          className="absolute right-0 top-0 z-10 flex rounded-sm border border-border bg-background shadow-sm"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className={cn(TOOLBAR_BTN_CLASS, "text-muted-foreground hover:text-foreground")}
            title={t("personaOpportunities.scripts.cell.copy", "Copy")}
            aria-label={t("personaOpportunities.scripts.cell.copy", "Copy")}
            onClick={() => void handleCopy()}
          >
            <Icon
              name={copied ? "check" : "copy"}
              size="xs"
              color="inherit"
              colorVar={copied ? "--success" : undefined}
              aria-hidden
            />
          </button>
          {canEdit ? (
            <button
              type="button"
              className={cn(TOOLBAR_BTN_CLASS, "text-muted-foreground hover:text-foreground")}
              title={t("personaOpportunities.scripts.cell.edit", "Edit")}
              aria-label={t("personaOpportunities.scripts.cell.edit", "Edit")}
              onClick={startEdit}
            >
              <Icon name="pencil" size="xs" color="inherit" aria-hidden />
            </button>
          ) : null}
          {canEdit && showAi ? (
            <button
              type="button"
              className={cn(TOOLBAR_BTN_CLASS, "text-[var(--ai-text)] hover:text-[var(--ai-text)]")}
              title={t("personaOpportunities.scripts.cell.aiEdit", "AI edit this cell")}
              aria-label={t("personaOpportunities.scripts.cell.aiEdit", "AI edit this cell")}
              onClick={startAi}
            >
              <Icon name="sparkles" size="xs" colorVar="--ai-text" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
